import React, { useState, useRef } from 'react';
import {
  MapPin, Search, X, CheckCircle,
  Users, Send, Lock, MessageCircle,
  AlertCircle, Briefcase, Clock, Plus, ChevronDown, ChevronUp, Image
} from 'lucide-react';

const subData = {
  'Nairobi': ['Westlands','Dagoretti North','Dagoretti South','Langata','Kibra','Roysambu','Kasarani','Ruaraka','Embakasi South','Embakasi North','Embakasi Central','Embakasi East','Embakasi West','Makadara','Kamukunji','Starehe','Mathare'],
  'Mombasa': ['Changamwe','Jomvu','Kisauni','Nyali','Likoni','Mvita'],
  'Kisumu': ['Kisumu East','Kisumu West','Kisumu Central','Seme','Nyando','Muhoroni','Nyakach'],
  'Nakuru': ['Nakuru Town East','Nakuru Town West','Naivasha','Gilgil','Molo','Njoro','Kuresoi North','Kuresoi South'],
  'Kiambu': ['Thika Town','Ruiru','Juja','Limuru','Kikuyu','Gatundu South','Gatundu North','Githunguri'],
  'Eldoret / Uasin Gishu': ['Kesses','Ainabkoi','Moiben','Turbo','Soy','Kapseret'],
  'Machakos': ['Machakos Town','Mavoko','Masinga','Yatta','Kangundo'],
  'Meru': ['Imenti North','Imenti South','Igembe South','Tigania East','Buuri'],
  'Kakamega': ['Shinyalu','Malava','Lurambi','Navakholo','Likuyani'],
  'Nyeri': ['Nyeri Town','Tetu','Mathira','Othaya','Mukurweini'],
};

const wardData = {
  'Westlands': ['Kitisuru','Parklands/Highridge','Karura','Kangemi','Mountain View'],
  'Dagoretti North': ['Kilimani','Kawangware','Gatina','Kileleshwa','Kabiro'],
  'Langata': ['Karen','Nairobi West','Mugumoini','South C','Nyayo Highrise'],
  'Kibra': ['Laini Saba','Lindi','Makina','Woodley/Kenyatta','Sarang\'ombe'],
  'Kasarani': ['Clay City','Mwiki','Kasarani','Njiru','Ruai'],
  'Changamwe': ['Port Reitz','Kipevu','Airport','Miritini','Chaani'],
  'Mvita': ['Tudor','Tononoka','Shimanzi/Ganjoni','Majengo','Mji wa Kale'],
};

function getWards(sub) {
  return wardData[sub] || ['Ward A', 'Ward B', 'Ward C', 'Ward D', 'Ward E'];
}

const MOCK_NAMES = ['Kamau','Akinyi','Otieno','Wanjiru','Mwenda','Fatuma','Njoroge','Chebet','Omondi','Muthoni'];

const DUMMY_TASKS = [
  {
    id: 10001, emoji: '🛠️',
    desc: 'I need a plumber — the bathroom pipe is leaking and flooding the floor.',
    county: 'Nairobi', sub: 'Westlands', ward: 'Parklands/Highridge',
    budget: 'KES 500–1,000', postedAgo: '10 mins ago',
    applicants: [{ name: 'Kamau', msg: 'I can make it by 2:00 PM.' }], applied: false, image: null
  },
  {
    id: 10002, emoji: '🚗',
    desc: 'Looking for a driver to take me from Karen to CBD early tomorrow morning at 6:00 AM.',
    county: 'Nairobi', sub: 'Langata', ward: 'Karen',
    budget: 'KES 300', postedAgo: '30 mins ago',
    applicants: [], applied: false, image: null
  },
  {
    id: 10003, emoji: '🌿',
    desc: 'Mow the backyard lawn — small compound, takes about 45 minutes. Tools are provided.',
    county: 'Nairobi', sub: 'Kasarani', ward: 'Clay City',
    budget: 'KES 400', postedAgo: '1 hour ago',
    applicants: [{ name: 'Omondi', msg: 'I am available right now.' }, { name: 'Njoroge', msg: 'I can come immediately.' }], applied: false, image: null
  }
];

export default function KaziConnect() {
  const [activeTab, setActiveTab] = useState('chukua');

  // Chat-style variables for Creation Tab
  const [createChat] = useState([
    { id: 1, sender: 'bot', text: 'Hello! Select your location and type out what you need done below just like a WhatsApp chat. You can also attach an image for context!' }
  ]);
  const [taskDesc, setTaskDesc] = useState('');
  const [taskBudget, setTaskBudget] = useState('');
  const [selCounty, setSelCounty] = useState('');
  const [selSub, setSelSub] = useState('');
  const [selWard, setSelWard] = useState('');
  const [attachedImage, setAttachedImage] = useState(null);
  const fileInputRef = useRef(null);

  // Expanded View states
  const [expandedTasks, setExpandedTasks] = useState(new Set());

  const [userTasks, setUserTasks] = useState([]);
  const [allTasks, setAllTasks] = useState(DUMMY_TASKS);
  const [unlockedTasks, setUnlockedTasks] = useState(new Set());

  const [fCounty, setFCounty] = useState('');
  const [fSub, setFSub] = useState('');
  const [searchQ, setSearchQ] = useState('');

  const [applyModal, setApplyModal] = useState(null);
  const [applyMsg, setApplyMsg] = useState('');
  const [applyChat, setApplyChat] = useState([]);

  const [applicantsModal, setApplicantsModal] = useState(null);
  const [payModal, setPayModal] = useState(null);

  const [chatModal, setChatModal] = useState(null);
  const [chatMsg, setChatMsg] = useState('');

  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('success');

  const showToast = (msg, type = 'success') => {
    setToast(msg); setToastType(type);
    setTimeout(() => setToast(''), 3000);
  };

  const handleCountyChange = (v) => { setSelCounty(v); setSelSub(''); setSelWard(''); };
  const handleSubChange = (v) => { setSelSub(v); setSelWard(''); };

  // Image Upload Processor
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedImage(reader.result);
        showToast('Image attached successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleExpandTask = (id) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Submits the chat conversation into a concrete card
  const submitTask = () => {
    if (!taskDesc.trim()) { showToast('Please type your task description', 'error'); return; }
    if (!selCounty) { showToast('Please select a county location first', 'error'); return; }
    
    const icons = ['🛠️','📦','🏠','🌿','🍳','💻','🔌','🎨','🚗','👶'];
    const newTask = {
      id: Date.now(),
      emoji: icons[Math.floor(Math.random() * icons.length)],
      desc: taskDesc.trim(),
      budget: taskBudget || 'Negotiable',
      county: selCounty, sub: selSub, ward: selWard,
      postedAgo: 'Just now',
      applicants: [], applied: false,
      image: attachedImage
    };

    setAllTasks(prev => [newTask, ...prev]);
    setUserTasks(prev => [newTask, ...prev]);
    
    // Clear Form fields
    setTaskDesc(''); setTaskBudget(''); setSelCounty(''); setSelSub(''); setSelWard(''); setAttachedImage(null);
    showToast('Task posted successfully! ✓');
    setActiveTab('chukua');
  };

  const openApply = (id) => {
    const t = allTasks.find(x => x.id === id);
    if (!t || t.applied) return;
    setApplyChat([{ me: false, text: 'Welcome! Send an introductory message. The full task will appear once the poster accepts your bid.' }]);
    setApplyMsg('');
    setApplyModal(id);
  };

  const sendApplyMsg = () => {
    if (!applyMsg.trim()) return;
    setApplyChat(prev => [...prev, { me: true, text: applyMsg.trim() }]);
    setApplyMsg('');
    setTimeout(() => {
      setApplyChat(prev => [...prev, { me: false, text: 'Your message has been received. Click "Confirm Application" to submit.' }]);
    }, 600);
  };

  const confirmApply = () => {
    const name = MOCK_NAMES[Math.floor(Math.random() * MOCK_NAMES.length)];
    setAllTasks(prev => prev.map(t =>
      t.id === applyModal
        ? { ...t, applied: true, applicants: [...t.applicants, { name, msg: 'I am ready to handle this task.' }] }
        : t
    ));
    setApplyModal(null);
    showToast('Application sent! 🎉');
  };

  const viewApplicants = (id) => {
    const t = allTasks.find(x => x.id === id);
    if (!t) return;
    if (!unlockedTasks.has(id) && t.applicants.length > 0) {
      setPayModal(id);
    } else {
      setApplicantsModal(id);
    }
  };

  const unlockApplicants = () => {
    setUnlockedTasks(prev => new Set([...prev, payModal]));
    const pid = payModal;
    setPayModal(null);
    showToast('Payment received! ✓');
    setTimeout(() => setApplicantsModal(pid), 400);
  };

  const openChat = (name) => {
    setApplicantsModal(null);
    setChatModal({ name, messages: [{ me: false, text: 'Hello! I am ready to work on your task. Tell me more about it.' }] });
    setChatMsg('');
  };

  const sendChat = () => {
    if (!chatMsg.trim()) return;
    const msg = chatMsg.trim();
    setChatModal(prev => ({ ...prev, messages: [...prev.messages, { me: true, text: msg }] }));
    setChatMsg('');
    setTimeout(() => {
      const replies = ['Alright, I will arrive at the requested time.', 'Thank you! I understand the requirements.', 'Great, see you soon.'];
      setChatModal(prev => prev ? ({ ...prev, messages: [...prev.messages, { me: false, text: replies[Math.floor(Math.random()*replies.length)] }] }) : prev);
    }, 900);
  };

  const filteredTasks = allTasks.filter(t => {
    const q = searchQ.toLowerCase();
    return (!fCounty || t.county === fCounty) &&
      (!fSub || t.sub === fSub) &&
      (!q || t.desc.toLowerCase().includes(q) || t.county.toLowerCase().includes(q));
  });

  const applyTask = applyModal ? allTasks.find(t => t.id === applyModal) : null;
  const applicantsTask = applicantsModal ? allTasks.find(t => t.id === applicantsModal) : null;

  const colors = {
    bg: '#0b141a',
    surface: '#1f2c34',
    surface2: '#2a3942',
    accent: '#00a884',
    accentDim: 'rgba(0,168,132,0.15)',
    text: '#e9edef',
    muted: '#8696a0',
    border: 'rgba(255,255,255,0.06)',
    danger: '#ef4444',
  };

  const s = {
    app: {
      minHeight: '100vh',
      background: colors.bg,
      color: colors.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: 14,
      maxWidth: 480,
      margin: '0 auto',
      position: 'relative',
    },
    navigationTabContainer: {
      background: colors.surface,
      position: 'sticky', top: 0, zIndex: 20,
      borderBottom: `1px solid ${colors.border}`,
    },
    tabBar: { display: 'flex', gap: 0 },
    tabBtn: (active) => ({
      flex: 1, padding: '12px 8px', border: 'none', cursor: 'pointer',
      background: 'transparent', color: active ? colors.accent : colors.muted,
      fontSize: 12, fontWeight: active ? 600 : 400,
      borderBottom: active ? `2px solid ${colors.accent}` : '2px solid transparent',
      transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
    }),
    formCard: {
      background: colors.surface,
      borderRadius: 16, padding: 16,
      border: `1px solid ${colors.border}`,
      marginBottom: 12,
    },
    label: { fontSize: 11, color: colors.muted, marginBottom: 6, fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase' },
    select: {
      width: '100%', background: colors.surface2, border: `1px solid ${colors.border}`, borderRadius: 12,
      color: colors.text, padding: '11px 14px', fontSize: 13, outline: 'none', cursor: 'pointer',
      appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238696a0' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
      paddingRight: 34, boxSizing: 'border-box',
    },
    searchBar: {
      display: 'flex', alignItems: 'center', gap: 8, background: colors.surface2,
      borderRadius: 12, padding: '0 14px', border: `1px solid ${colors.border}`,
      marginBottom: 12,
    },
    searchInput: {
      flex: 1, background: 'none', border: 'none', color: colors.text, padding: '11px 0',
      fontSize: 13, outline: 'none', fontFamily: 'inherit',
    },
    filterRow: { display: 'flex', gap: 8, marginBottom: 14 },
    filterSelect: {
      flex: 1, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10,
      color: colors.text, padding: '9px 12px', fontSize: 12, outline: 'none',
      cursor: 'pointer', appearance: 'none', paddingRight: 28,
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%238696a0' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
    },
    taskCard: {
      background: colors.surface, borderRadius: 16, marginBottom: 10,
      border: `1px solid ${colors.border}`, overflow: 'hidden',
    },
    taskCardHeader: { padding: '14px 14px 10px' },
    taskEmoji: {
      width: 40, height: 40, borderRadius: 10, background: colors.surface2,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
    },
    taskTitle: { fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: colors.text },
    taskMeta: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: colors.muted, marginTop: 8 },
    taskFooter: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', gap: 10, borderTop: `1px solid ${colors.border}`, background: 'rgba(0,0,0,0.1)'
    },
    budgetChip: {
      background: colors.accentDim, color: colors.accent,
      borderRadius: 20, fontSize: 11, fontWeight: 600, padding: '3px 10px',
    },
    applyBtn: (applied) => ({
      background: applied ? colors.surface2 : colors.accent,
      color: applied ? colors.muted : 'white', border: 'none', borderRadius: 10,
      padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: applied ? 'default' : 'pointer',
      display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
    }),
    myTaskCard: {
      background: colors.surface, borderRadius: 14, padding: 14, marginBottom: 10,
      border: `1px solid ${colors.border}`,
    },
    viewBtn: {
      width: '100%', background: colors.accentDim, color: colors.accent, border: 'none',
      borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 5,
    },
    modalBg: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50,
      display: 'flex', alignItems: 'flex-end',
    },
    modal: {
      background: colors.surface, borderRadius: '20px 20px 0 0',
      width: '100%', maxWidth: 480, margin: '0 auto',
      padding: '20px 16px 32px', position: 'relative',
      maxHeight: '88vh', overflowY: 'auto',
    },
    modalHandle: {
      width: 36, height: 4, background: colors.surface2, borderRadius: 2,
      margin: '0 auto 18px', display: 'block',
    },
    closeBtn: {
      position: 'absolute', top: 16, right: 16, background: colors.surface2, border: 'none',
      color: colors.muted, borderRadius: '50%', width: 30, height: 30,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    chatWrap: { display: 'flex', flexDirection: 'column', minHeight: 120, maxHeight: 220, overflowY: 'auto', marginBottom: 10, gap: 6 },
    chatBubble: (me) => ({
      background: me ? '#005c4b' : colors.surface2,
      borderRadius: me ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
      padding: '9px 13px', fontSize: 13, color: colors.text,
      maxWidth: '82%', alignSelf: me ? 'flex-end' : 'flex-start', lineHeight: 1.4,
    }),
    applicantRow: {
      display: 'flex', alignItems: 'center', gap: 12, background: colors.bg,
      borderRadius: 12, padding: '12px 14px', marginBottom: 8, border: `1px solid ${colors.border}`,
    },
    avatarLg: {
      width: 40, height: 40, borderRadius: '50%', background: colors.accentDim,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, fontWeight: 600, color: colors.accent, flexShrink: 0,
    },
    chatInputRow: { display: 'flex', gap: 8, alignItems: 'center' },
    chatSendBtn: {
      background: colors.accent, border: 'none', borderRadius: 10,
      width: 40, height: 40, color: 'white', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    toast: (type) => ({
      position: 'fixed', bottom: 24, left: 16, right: 16, maxWidth: 448, margin: '0 auto',
      background: type === 'error' ? colors.danger : '#1a3d34',
      border: `1px solid ${type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(0,168,132,0.3)'}`,
      color: 'white', padding: '13px 16px', borderRadius: 12, fontSize: 13, zIndex: 100,
      display: 'flex', alignItems: 'center', gap: 9, boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
    }),
    emptyState: { textAlign: 'center', padding: '48px 16px', color: colors.muted },
    badge: { background: colors.accent, color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700, marginLeft: 5 },
    btnActionPost: {
      width: '100%', background: colors.accent, color: 'white', border: 'none',
      borderRadius: 12, padding: '12px 20px', fontSize: 14, fontWeight: 600,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    },
    lockWrapperIcon: {
      width: 48, height: 48, borderRadius: '50%', background: colors.surface2,
      fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
    },
    chatHeaderIconName: {
      width: 26, height: 26, borderRadius: '50%', background: colors.accentDim,
      fontSize: 12, fontWeight: 600, color: colors.accent, display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    chatTriggerBtn: {
      background: colors.accentDim, color: colors.accent, border: 'none', borderRadius: 8,
      padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 4,
    }
  };

  return (
    <div style={s.app}>
      {/* Toast Notification */}
      {toast && (
        <div style={s.toast(toastType)}>
          {toastType === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          {toast}
        </div>
      )}

      {/* CORE NAVIGATION BAR */}
      <div style={s.navigationTabContainer}>
        <div style={s.tabBar}>
          <button style={s.tabBtn(activeTab === 'toa')} onClick={() => setActiveTab('toa')}>
            <Plus size={14} /> Post a Task
          </button>
          <button style={s.tabBtn(activeTab === 'chukua')} onClick={() => setActiveTab('chukua')}>
            <Search size={14} /> Find Tasks
            {allTasks.length > 0 && <span style={s.badge}>{allTasks.length}</span>}
          </button>
          <button style={s.tabBtn(activeTab === 'zangu')} onClick={() => setActiveTab('zangu')}>
            <Briefcase size={14} /> My Tasks
            {userTasks.length > 0 && <span style={s.badge}>{userTasks.length}</span>}
          </button>
        </div>
      </div>

      {/* POST A TASK */}
      {activeTab === 'toa' && (
        <div style={{ padding: '16px 16px 100px' }}>
          
          <div style={s.formCard}>
            <div style={s.label}>1. First Choose Location</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <select style={s.select} value={selCounty} onChange={e => handleCountyChange(e.target.value)}>
                <option value="">Select County</option>
                {Object.keys(subData).map(c => <option key={c}>{c}</option>)}
              </select>
              <select style={s.select} value={selSub} disabled={!selCounty} onChange={e => handleSubChange(e.target.value)}>
                <option value="">Select Sub-County</option>
                {selCounty && (subData[selCounty] || []).map(s => <option key={s}>{s}</option>)}
              </select>
              <select style={s.select} value={selWard} disabled={!selSub} onChange={e => setSelWard(e.target.value)}>
                <option value="">Select Ward (optional)</option>
                {selSub && getWards(selSub).map(w => <option key={w}>{w}</option>)}
              </select>
            </div>
          </div>

          <div style={{ ...s.formCard, background: '#0b141a', backgroundImage: 'radial-gradient(rgba(0,168,132,0.04) 15%, transparent 16%)', backgroundSize: '16px 16px' }}>
            <div style={s.label}>2. Chat details & attach image</div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '10px 0 20px' }}>
              {createChat.map(m => (
                <div key={m.id} style={s.chatBubble(m.sender === 'user')}>
                  {m.text}
                </div>
              ))}
              
              {taskDesc && (
                <div style={s.chatBubble(true)}>
                  <strong>Task Description:</strong> {taskDesc}
                </div>
              )}
              {taskBudget && (
                <div style={s.chatBubble(true)}>
                  <strong>Proposed Budget:</strong> {taskBudget}
                </div>
              )}
              {attachedImage && (
                <div style={{ ...s.chatBubble(true), padding: 6 }}>
                  <img src={attachedImage} alt="Attachment" style={{ width: '100%', maxHeight: 150, borderRadius: 8, objectFit: 'cover' }} />
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4, textAlign: 'right' }}>Image Attached 📎</div>
                </div>
              )}
            </div>

            <div style={{ background: colors.surface, borderRadius: 14, padding: 10, border: `1px solid ${colors.border}` }}>
              <textarea
                style={{ width: '100%', background: 'transparent', border: 'none', color: colors.text, outline: 'none', fontSize: 13, resize: 'none', fontFamily: 'inherit' }}
                placeholder="Type your task details here..."
                rows={2}
                value={taskDesc}
                onChange={e => setTaskDesc(e.target.value)}
              />
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderTop: `1px solid ${colors.border}`, paddingTop: 8, marginTop: 4 }}>
                <input 
                  type="text"
                  placeholder="Budget e.g KES 800"
                  style={{ background: colors.surface2, border: 'none', color: colors.text, borderRadius: 8, padding: '6px 10px', fontSize: 12, flex: 1, outline: 'none' }}
                  value={taskBudget}
                  onChange={e => setTaskBudget(e.target.value)}
                />
                
                <input 
                  type="file" 
                  accept="image/*" 
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  onChange={handleImageUpload}
                />
                <button 
                  onClick={() => fileInputRef.current.click()} 
                  style={{ background: attachedImage ? colors.accentDim : 'none', border: 'none', color: attachedImage ? colors.accent : colors.muted, cursor: 'pointer', padding: 6, borderRadius: 6 }}
                  title="Attach Image"
                >
                  <Image size={20} />
                </button>
              </div>
            </div>
          </div>

          <button style={s.btnActionPost} onClick={submitTask}>
            <Send size={16} /> Post Task
          </button>
        </div>
      )}

      {/* FIND TASKS */}
      {activeTab === 'chukua' && (
        <div style={{ padding: '14px 16px 80px' }}>
          <div style={s.searchBar}>
            <Search size={15} color={colors.muted} />
            <input style={s.searchInput} placeholder="Search tasks..." value={searchQ} onChange={e => setSearchQ(e.target.value)} />
            {searchQ && <button style={{ background: 'none', border: 'none', color: colors.muted }} onClick={() => setSearchQ('')}><X size={14} /></button>}
          </div>

          <div style={s.filterRow}>
            <select style={s.filterSelect} value={fCounty} onChange={e => { setFCounty(e.target.value); setFSub(''); }}>
              <option value="">All Counties</option>
              {Object.keys(subData).map(c => <option key={c}>{c}</option>)}
            </select>
            <select style={s.filterSelect} value={fSub} disabled={!fCounty} onChange={e => setFSub(e.target.value)}>
              <option value="">All Sub-Counties</option>
              {fCounty && (subData[fCounty] || []).map(s => <option key={s}>{s}</option>)}
            </select>
          </div>

          {filteredTasks.map(t => {
            const isExpanded = expandedTasks.has(t.id);
            return (
              <div key={t.id} style={s.taskCard}>
                <div style={s.taskCardHeader}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={s.taskEmoji}>{t.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div 
                        style={{ ...s.taskTitle, cursor: 'pointer' }}
                        onClick={() => toggleExpandTask(t.id)}
                      >
                        {isExpanded ? t.desc : (t.desc.length > 75 ? t.desc.substring(0, 75) + '...' : t.desc)}
                      </div>
                      
                      {isExpanded && (
                        <div style={{ marginTop: 12, padding: 10, background: colors.surface2, borderRadius: 10 }}>
                          <div style={{ fontSize: 12, marginBottom: 4 }}><strong>Full Details:</strong></div>
                          <div style={{ fontSize: 13, color: colors.text, marginBottom: 8, lineHeight: 1.4 }}>{t.desc}</div>
                          <div style={{ fontSize: 12, color: colors.muted }}>
                            📍 <strong>Ward Location:</strong> {t.ward || 'Not specified'}
                          </div>
                          {t.image && (
                            <div style={{ marginTop: 10 }}>
                              <img src={t.image} alt="Contextual capture" style={{ width: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'cover' }} />
                            </div>
                          )}
                        </div>
                      )}

                      <div style={s.taskMeta}>
                        <MapPin size={11} />
                        <span>{t.county}{t.sub ? ' · ' + t.sub : ''}</span>
                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Clock size={10} />{t.postedAgo}
                        </span>
                        
                        <button 
                          onClick={() => toggleExpandTask(t.id)}
                          style={{ background: 'none', border: 'none', color: colors.accent, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', padding: '0 4px' }}
                        >
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          <span style={{ fontSize: 11 }}>{isExpanded ? 'Less' : 'More'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={s.taskFooter}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {t.budget && <span style={s.budgetChip}>{t.budget}</span>}
                  </div>
                  <button style={s.applyBtn(t.applied)} onClick={() => openApply(t.id)} disabled={t.applied}>
                    {t.applied ? <><CheckCircle size={13} /> Applied</> : <><Send size={13} /> Apply</>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MY POSTED TASKS */}
      {activeTab === 'zangu' && (
        <div style={{ padding: '16px 16px 80px' }}>
          {userTasks.length === 0 ? (
            <div style={s.emptyState}>
              <Briefcase size={40} style={{ display: 'block', margin: '0 auto 12px', opacity: 0.3 }} />
              <div style={{ fontWeight: 500 }}>You haven't posted any tasks yet</div>
            </div>
          ) : (
            userTasks.map(t => (
              <div key={t.id} style={s.myTaskCard}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ ...s.taskEmoji, width: 36, height: 36, fontSize: 18 }}>{t.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{t.desc}</div>
                    <div style={{ fontSize: 11, color: colors.muted, marginTop: 4 }}><MapPin size={10} /> {t.county} · {t.sub}</div>
                  </div>
                </div>
                <button style={s.viewBtn} onClick={() => viewApplicants(t.id)}>
                  <Users size={14} /> Applicants ({t.applicants.length})
                  {t.applicants.length > 0 && !unlockedTasks.has(t.id) && <Lock size={12} style={{ marginLeft: 'auto' }} />}
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* OVERLAY MODALS */}
      {applyModal && applyTask && (
        <div style={s.modalBg}>
          <div style={s.modal}>
            <span style={s.modalHandle} />
            <button style={s.closeBtn} onClick={() => setApplyModal(null)}><X size={16} /></button>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Apply for: {applyTask.desc.substring(0,30)}...</div>
            
            <div style={s.chatWrap}>
              {applyChat.map((c, i) => <div key={i} style={s.chatBubble(c.me)}>{c.text}</div>)}
            </div>

            <div style={s.chatInputRow}>
              <input style={{ ...s.searchInput, background: colors.surface2, borderRadius: 10, padding: 10 }} placeholder="Type introductory message..." value={applyMsg} onChange={e => setApplyMsg(e.target.value)} />
              <button style={s.chatSendBtn} onClick={sendApplyMsg}><Send size={14} /></button>
            </div>
            <button style={{ ...s.btnActionPost, marginTop: 14 }} onClick={confirmApply}>Confirm Application</button>
          </div>
        </div>
      )}

      {payModal && (
        <div style={s.modalBg}>
          <div style={s.modal}>
            <span style={s.modalHandle} />
            <button style={s.closeBtn} onClick={() => setPayModal(null)}><X size={16} /></button>
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={s.lockWrapperIcon}>🔒</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Unlock Applicant Details</div>
              <div style={{ fontSize: 13, color: colors.muted, marginBottom: 16 }}>Access full profiles, ratings, and instant contact channels.</div>
              <div style={{ background: colors.bg, borderRadius: 12, padding: '14px 16px', marginBottom: 14, textAlign: 'center', border: `1px solid ${colors.border}` }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: colors.accent }}>KES 100</span> per unlock
              </div>
              <button style={{ background: '#25d366', color: '#0d1b0f', border: 'none', borderRadius: 12, padding: '14px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer', width: '100%' }} onClick={unlockApplicants}>Pay via M-PESA</button>
            </div>
          </div>
        </div>
      )}

      {applicantsModal && applicantsTask && (
        <div style={s.modalBg}>
          <div style={s.modal}>
            <span style={s.modalHandle} />
            <button style={s.closeBtn} onClick={() => setApplicantsModal(null)}><X size={16} /></button>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Applicants ({applicantsTask.applicants.length})</div>
            {applicantsTask.applicants.map((a, i) => (
              <div key={i} style={s.applicantRow}>
                <div style={s.avatarLg}>{a.name[0]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{a.name}</div>
                  <div style={{ fontSize: 12, color: colors.muted }}>{a.msg}</div>
                </div>
                <button style={s.chatTriggerBtn} onClick={() => openChat(a.name)}><MessageCircle size={13} /> Chat</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {chatModal && (
        <div style={s.modalBg}>
          <div style={s.modal}>
            <span style={s.modalHandle} />
            <button style={s.closeBtn} onClick={() => setChatModal(null)}><X size={16} /></button>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={s.chatHeaderIconName}>{chatModal.name[0]}</div>
              {chatModal.name}
            </div>
            
            <div style={s.chatWrap}>
              {chatModal.messages.map((m, i) => <div key={i} style={s.chatBubble(m.me)}>{m.text}</div>)}
            </div>

            <div style={s.chatInputRow}>
              <input style={{ ...s.searchInput, background: colors.surface2, borderRadius: 10, padding: 10 }} placeholder="Type a message..." value={chatMsg} onChange={e => setChatMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendChat()} />
              <button style={s.chatSendBtn} onClick={sendChat}><Send size={14} /></button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}